/**
 * Prescription presentation contracts, shared by the visits feature (which authors and prints prescriptions) and
 * the settings feature (which configures branding/format and previews the result). Kept in `shared` because both
 * features need them and cross-feature imports are not allowed.
 *
 * Mirrors DocCarePlus.Contracts.Visits.Common.PrescriptionLayoutResponse.
 */

/** One of the five generic prescription item slots. Mirrors Domain.Settings.PrescriptionSlotKey. */
export type PrescriptionSlotKey = 'MedicineName' | 'Dose' | 'Frequency' | 'Duration' | 'Instructions';

/** One printable prescription section. Mirrors Domain.Settings.PrescriptionSectionKey. */
export type PrescriptionSectionKey =
  | 'Vitals'
  | 'Complaint'
  | 'Diagnosis'
  | 'Investigations'
  | 'Prescription'
  | 'Advice'
  | 'FollowUp';

export type PrintMode = 'DigitalLetterhead' | 'PrePrintedStationery';
export type PrescriptionPageSize = 'A4' | 'A5' | 'Letter' | 'Custom';

/** Every slot key, in the canonical preset order — for settings UI that enumerates them. */
export const ALL_SLOT_KEYS: PrescriptionSlotKey[] = [
  'MedicineName',
  'Dose',
  'Frequency',
  'Duration',
  'Instructions',
];

/** Every section key, in the canonical preset order — for settings UI that enumerates them. */
export const ALL_SECTION_KEYS: PrescriptionSectionKey[] = [
  'Vitals',
  'Complaint',
  'Diagnosis',
  'Investigations',
  'Prescription',
  'Advice',
  'FollowUp',
];

/**
 * The clinic's resolved print branding. Visibility toggles are applied server-side, so a field the clinic chose to
 * hide arrives absent rather than flagged — render exactly what is present and never substitute a placeholder.
 */
export interface PrescriptionBrandingResponse {
  clinicName?: string | null;
  logoUrl?: string | null;
  registrationNumber?: string | null;
  addressLine?: string | null;
  phone?: string | null;
  email?: string | null;
  headerSubtitle?: string | null;
  accentColor: string;
  defaultPrintMode: PrintMode;
  pageSize?: PrescriptionPageSize;
  pageWidthMm?: number;
  pageHeightMm?: number;
  customPageWidthMm?: number | null;
  customPageHeightMm?: number | null;
  topMarginMm: number;
  bottomMarginMm: number;
  leftMarginMm: number;
  rightMarginMm: number;
  footerLines: string[];
  showPageNumber: boolean;
  showDigitallySignedNotice: boolean;
}

/**
 * A label arrives as a translatable `labelKey` plus an optional clinic-authored `labelOverride`. Render the
 * override when present and translate the key otherwise — clinic text cannot be translated.
 */
export interface PrescriptionFormatSectionResponse {
  key: PrescriptionSectionKey;
  labelKey: string;
  labelOverride?: string | null;
}

export interface PrescriptionFormatSlotResponse {
  key: PrescriptionSlotKey;
  labelKey: string;
  labelOverride?: string | null;
  /** Suggestion vocabulary for this slot, already resolved from preset + clinic override. */
  vocabulary: string[];
}

/** The clinic's effective prescription format: its clinic-type preset with per-clinic overrides applied. */
export interface PrescriptionFormatResponse {
  headingKey: string;
  headingOverride?: string | null;
  sections: PrescriptionFormatSectionResponse[];
  slots: PrescriptionFormatSlotResponse[];
}

/** Everything needed to render a prescription's chrome. */
export interface PrescriptionLayoutResponse {
  branding: PrescriptionBrandingResponse;
  format: PrescriptionFormatResponse;
  /**
   * The clinic's system-of-medicine preset with no overrides applied. Only the clinic-settings editor uses it, to
   * preview unsaved override edits without saving first. Render surfaces should use `format`.
   */
  preset?: PrescriptionFormatResponse | null;
}

/**
 * The clinic's bounded prescription-format overrides, as the settings form edits them. Keys are section/slot enum
 * names. Mirrors Contracts.Settings.Common.PrescriptionFormatOverrides{Request,Response}.
 */
export interface PrescriptionFormatOverrides {
  hiddenSections: string[];
  sectionLabels: Record<string, string>;
  hiddenSlots: string[];
  slotLabels: Record<string, string>;
  slotVocabularies: Record<string, string[]>;
  rxHeading?: string | null;
}

/** Overrides nothing: the clinic uses its preset verbatim. */
export const EMPTY_FORMAT_OVERRIDES: PrescriptionFormatOverrides = {
  hiddenSections: [],
  sectionLabels: {},
  hiddenSlots: [],
  slotLabels: {},
  slotVocabularies: {},
  rxHeading: null,
};

/** The minimal patient identity the paper's header line needs. */
export interface PrescriptionPatientHeader {
  patientNumber: string;
  fullName: string;
  age?: number | null;
  gender?: string | null;
}

export interface PrescriptionDocumentProvider {
  name: string;
  qualification?: string | null;
  registrationNumber?: string | null;
}

export interface PrescriptionDocumentItem {
  medicineName: string;
  dose?: string | null;
  frequency?: string | null;
  duration?: string | null;
  instructions?: string | null;
  order: number;
}

/**
 * Presentation-only prescription data. The clinic app maps a visit/signed snapshot into this shape while settings
 * passes representative data. Both preview and print therefore render the exact same document component.
 */
export interface PrescriptionDocumentData {
  provider: PrescriptionDocumentProvider;
  signedByName?: string | null;
  patient: PrescriptionPatientHeader;
  visitDate: string;
  allergy?: string | null;
  vitals: string[];
  complaint?: string | null;
  diagnosis?: string | null;
  investigations: string[];
  items: PrescriptionDocumentItem[];
  advice?: string | null;
  followUpDate?: string | null;
  /** Guest share-link URL for this prescription, minted fresh for the current print/preview. Absent when not shareable. */
  shareUrl?: string | null;
}
